import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Settings, ChevronDown, User as UserIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettingsModal } from '../context/SettingsModalContext';
import logoDark from '../assets/brand/logo-dark.png';

// Shared header for app-shell pages that don't have their own bespoke one
// (Home.jsx and Login.jsx keep their existing inline headers - Home's
// hasn't been redesigned yet in this rollout). Uses the RUDVAN brand tokens
// (tailwind.config.js) - navy background, brand-blue/cyan gradient accents,
// the real wordmark logo (same asset as the footer/login modal - this
// header's background is always navy, so the dark-mode variant is correct
// unconditionally, no theme prop here). The "Back to home" button was
// removed (docs/DECISIONS.md, "Persistent left-side student navigation +
// light-theme visual fix" follow-up) - every page using this header is now
// wrapped in the persistent StudentSidebar, which already covers
// navigation; clicking the logo does the same job instead. The top-right
// profile icon is a Settings/Logout dropdown (same follow-up, user's
// explicit ask) - duplicated with the sidebar's own Settings/Logout by
// design, a standard top-right-profile-menu pattern.
export default function AppHeader({ subtitle }) {
  const { user, logout } = useAuth();
  const { openSettings } = useSettingsModal();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    if (!window.confirm('Do you really want to logout?')) return;
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-50 bg-navy/95 backdrop-blur-md border-b border-white/10 px-4 sm:px-6 py-4">
      <div className="flex items-center justify-between max-w-6xl mx-auto">
        <button
          type="button"
          onClick={() => navigate('/practice')}
          className="flex items-center gap-4 min-w-0 ml-10 lg:ml-0"
          aria-label="Go to home"
        >
          {/* ml-10 below lg: clears StudentSidebar's floating mobile-menu
              button (`fixed top-3 left-3 z-[10000]`), which otherwise sat
              on top of the "R" in this logo (reported live on a real
              device, docs/DECISIONS.md, "Student-facing pages mobile
              layout" decision - same fix already applied to Home.jsx/
              CurriculumMenuPage.jsx's own bespoke headers). */}
          <img src={logoDark} alt="RUDVAN" className="h-8 sm:h-11 w-auto flex-shrink-0" />
          {subtitle && <p className="text-sm text-white/60 mt-0.5 truncate hidden sm:block">{subtitle}</p>}
        </button>

        {user && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 hover:bg-white/10 rounded-btn transition-colors"
            >
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
              ) : (
                <UserIcon size={16} className="text-white/70" />
              )}
              <span className="text-xs text-white/70 hidden sm:inline">{user.name?.split(' ')[0] || user.email}</span>
              <ChevronDown size={14} className={`transition-transform text-white/50 ${menuOpen ? 'rotate-180' : ''}`} />
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-48 rounded-card border border-gray-200 bg-white shadow-brand-lg overflow-hidden text-navy z-20">
                <button
                  onClick={() => { setMenuOpen(false); openSettings(); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-surface-light transition-colors text-left"
                >
                  <Settings size={16} className="text-text-slate" />
                  Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left border-t border-gray-100"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
